import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import ChatSvg from 'images/v5/icons/annotation.svg';
import CircleExlamationSvg from 'images/v5/icons/circle/exclamation.svg';
import DuplicateSvg from 'images/v5/icons/copy-01.svg';
import DotsSvg from 'images/v5/icons/dots-vertical.svg';
import ExpandSvg from 'images/v5/icons/expand-01.svg';
import FileSvg from 'images/v5/icons/file-04.svg';
import FileAttachmentSvg from 'images/v5/icons/file-attachment-03.svg';
import Button from 'layout/atoms/Button/Button';
import ColorDot from 'layout/atoms/ColorDot';
import Dropdown from 'layout/atoms/Dropdown/Dropdown';
import Loader from 'layout/atoms/Loader/Loader';
import ParameterDetails from 'layout/atoms/ParameterDetails/ParameterDetails';
import Sidebar from 'layout/atoms/Sidebar/Sidebar';
import TabsButton from 'layout/atoms/TabsButton/TabsButton';
import Grid from 'layout/molecules/Grid/Grid';
import { closeSidebar, openModal, openSidebar, selectSidebarByName } from 'store/layoutSlice';
import { setPrevPath } from 'store/specificationDetailsSlice';
import { fetchSpecificationPreview, resetSpecificationPreview, } from 'store/specificationPreviewSlice';
import getStatusLabel from 'utils/getStatusLabel';
import { getStatusColorVar } from 'utils/statusColors';
import ComponentCostsPriceDiff from '../SpecificationDetails/ComponentCostsPriceDiff/ComponentCostsPriceDiff';
import Comments from './Comments';
import ComponentCosts from './ComponentCosts';
import Components from './Components';
import Consumptions from './Consumptions';
import EstimatedCosts from './EstimatedCosts';
import Info from './Info';
import Quantities from './Quantities';
import Variants from './Variants';
function SpecificationPreview() {
    var _a, _b, _c;
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { t, i18n } = useTranslation(['specification', 'common']);
    const storeData = useSelector((state) => state);
    const sidebar = useSelector(selectSidebarByName('specificationPreview'));
    const id = sidebar === null || sidebar === void 0 ? void 0 : sidebar.id;
    const { status } = storeData.specificationPreview;
    const fetchStatus = status.fetch;
    const specificationDetails = storeData.specificationPreview.item;
    const [activeTab, setActiveTab] = useState('info');
    useEffect(() => {
        if (id) {
            dispatch(fetchSpecificationPreview({ specificationId: id }));
        }
        return () => {
            dispatch(resetSpecificationPreview());
        };
    }, [dispatch, id]);
    const handleDatailsOpen = () => {
        dispatch(setPrevPath({ path: location.pathname }));
        navigate(`/specifications/details/${id}/summary`);
    };
    const handleChatOpen = () => {
        dispatch(openSidebar({
            name: 'chat',
            data: { id: `/specifications/details/${id}`, title: specificationDetails.itemCode },
        }));
    };
    const tabs = [
        { id: 'info', label: t('info') },
        { id: 'quantities', label: t('production_quantities') },
        { id: 'components', label: t('components') },
        { id: 'consumptions', label: t('CONSUMPTIONS') },
        { id: 'variants', label: t('component_variants') },
        { id: 'componentCosts', label: t('component_costs') },
        { id: 'estimatedCosts', label: t('estimated_costs') },
        { id: 'comments', label: t('comments') },
    ];
    if (fetchStatus === 'loading') {
        return (React.createElement(Sidebar, { name: "specificationPreview", header: React.createElement("div", null) },
            React.createElement(Grid, null,
                React.createElement(Loader, null))));
    }
    return (specificationDetails && (React.createElement(React.Fragment, null,
        React.createElement(Sidebar, { name: "specificationPreview", header: React.createElement(React.Fragment, null,
                (specificationDetails === null || specificationDetails === void 0 ? void 0 : specificationDetails.state) === 'DRAFT' &&
                    (((_b = (_a = specificationDetails === null || specificationDetails === void 0 ? void 0 : specificationDetails.confirmationProblems) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0) > 0 ? (React.createElement(Button, { size: "xs", theme: "fill", color: "danger", leftIcon: React.createElement(CircleExlamationSvg, null), onClick: () => dispatch(openModal({ name: 'specificationProblems', data: { preview: true } })) }, t('specification_problems'))) : (React.createElement(Button, { size: "xs", theme: "ghost", disabled: true, leftIcon: React.createElement(ColorDot, { color: getStatusColorVar((_c = specificationDetails === null || specificationDetails === void 0 ? void 0 : specificationDetails.status) === null || _c === void 0 ? void 0 : _c.color), draft: !(specificationDetails === null || specificationDetails === void 0 ? void 0 : specificationDetails.status) }) }, getStatusLabel(specificationDetails === null || specificationDetails === void 0 ? void 0 : specificationDetails.status, i18n) || t('common:draft')))),
                (specificationDetails === null || specificationDetails === void 0 ? void 0 : specificationDetails.state) === 'PRODUCTION' && (React.createElement(Button, { size: "xs", theme: "ghost", disabled: true, leftIcon: React.createElement(ColorDot, { color: "var(--semantic-text-onsubtle-positive)", check: true }) }, t('common:confirmed')))), headerAside: React.createElement(React.Fragment, null,
                React.createElement(Dropdown, { trigger: React.createElement(Button, { size: "s", shape: "rounded", theme: "ghost", leftIcon: React.createElement(DotsSvg, null) }), position: "bottomRight" },
                    (specificationDetails === null || specificationDetails === void 0 ? void 0 : specificationDetails.state) !== 'DRAFT' && (React.createElement(Button, { align: "left", theme: "ghost", leftIcon: React.createElement(DuplicateSvg, null), onClick: () => dispatch(openModal({ name: 'duplicateSpecification', id: specificationDetails === null || specificationDetails === void 0 ? void 0 : specificationDetails.id })) }, t('common:duplicate_as_draft'))),
                    React.createElement(Button, { align: "left", theme: "ghost", leftIcon: React.createElement(ChatSvg, null), onClick: handleChatOpen }, t('common:chat')),
                    React.createElement("hr", null),
                    React.createElement(Button, { align: "left", theme: "ghost", leftIcon: React.createElement(FileSvg, null), to: `/specifications/pdf/${specificationDetails === null || specificationDetails === void 0 ? void 0 : specificationDetails.id}`, onClick: () => dispatch(closeSidebar({ name: 'specificationPreview' })) }, t('GENERATE_PDF_BUTTON')),
                    React.createElement(Button, { align: "left", theme: "ghost", leftIcon: React.createElement(FileAttachmentSvg, null), to: `/specifications/pdf-components/${specificationDetails === null || specificationDetails === void 0 ? void 0 : specificationDetails.id}` }, t('GENERATE_PDF_WITH_COMPONENTS_BUTTON'))),
                React.createElement(Button, { size: "s", shape: "rounded", theme: "ghost", leftIcon: React.createElement(ExpandSvg, null), onClick: (specificationDetails === null || specificationDetails === void 0 ? void 0 : specificationDetails.state) === 'DRAFT'
                        ? () => dispatch(openModal({
                            name: 'specificationCreator',
                            data: { id: specificationDetails === null || specificationDetails === void 0 ? void 0 : specificationDetails.id },
                        }))
                        : handleDatailsOpen })) },
            React.createElement(ParameterDetails, { size: "xl", label: t('marketing_name'), value: (specificationDetails === null || specificationDetails === void 0 ? void 0 : specificationDetails.fancyName) || '-' }),
            React.createElement(TabsButton, { tabs: tabs, activeTab: activeTab, onClick: (e) => setActiveTab(e) }),
            {
                info: React.createElement(Info, null),
                quantities: React.createElement(Quantities, null),
                components: React.createElement(Components, null),
                consumptions: React.createElement(Consumptions, null),
                variants: React.createElement(Variants, null),
                componentCosts: React.createElement(ComponentCosts, null),
                estimatedCosts: React.createElement(EstimatedCosts, null),
                comments: React.createElement(Comments, null),
            }[activeTab]),
        storeData.layout.showModals.some((modal) => modal.name === 'componentCostsPriceDiff') && (React.createElement(ComponentCostsPriceDiff, null)))));
}
export default SpecificationPreview;
